/**
 * Thin wrapper around `window.Telegram.WebApp` with graceful noops when
 * not running inside Telegram (dev browser, tests, SSR).
 */

type TelegramUser = {
  id: number;
  first_name?: string;
  last_name?: string;
  username?: string;
  language_code?: string;
};

type TelegramWebApp = {
  initData: string;
  initDataUnsafe: {
    user?: TelegramUser;
    start_param?: string;
  };
  version: string;
  platform: string;
  colorScheme: 'light' | 'dark';
  themeParams: Record<string, string>;
  ready(): void;
  expand(): void;
  close(): void;
  openLink(url: string): void;
  openTelegramLink(url: string): void;
  HapticFeedback?: {
    impactOccurred(style: 'light' | 'medium' | 'heavy' | 'rigid' | 'soft'): void;
    notificationOccurred(type: 'error' | 'success' | 'warning'): void;
    selectionChanged(): void;
  };
  BackButton: {
    show(): void;
    hide(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };
  MainButton: {
    setText(t: string): TelegramWebApp['MainButton'];
    show(): void;
    hide(): void;
    enable(): void;
    disable(): void;
    onClick(cb: () => void): void;
    offClick(cb: () => void): void;
  };
};

declare global {
  interface Window {
    Telegram?: { WebApp: TelegramWebApp };
  }
}

// Lazy access on every call — `?mock=1` mode installs a stub
// `window.Telegram.WebApp` *after* this module is imported (during
// `main.tsx` bootstrap); a captured-at-import reference would stay
// null forever and the mock would never activate.
function getWebApp(): TelegramWebApp | null {
  if (typeof window === 'undefined') return null;
  return window.Telegram?.WebApp ?? null;
}

export const tg = {
  get initData(): string {
    return getWebApp()?.initData ?? '';
  },
  get user(): TelegramUser | null {
    return getWebApp()?.initDataUnsafe?.user ?? null;
  },
  get startParam(): string | null {
    return getWebApp()?.initDataUnsafe?.start_param ?? null;
  },
  get inTelegram(): boolean {
    const w = getWebApp();
    return w !== null && !!w.initData;
  },
  get colorScheme(): 'light' | 'dark' | null {
    return getWebApp()?.colorScheme ?? null;
  },
  ready(): void {
    getWebApp()?.ready();
  },
  expand(): void {
    getWebApp()?.expand();
  },
  close(): void {
    getWebApp()?.close();
  },
  hapticLight(): void {
    getWebApp()?.HapticFeedback?.impactOccurred('light');
  },
  hapticSuccess(): void {
    getWebApp()?.HapticFeedback?.notificationOccurred('success');
  },
  hapticError(): void {
    getWebApp()?.HapticFeedback?.notificationOccurred('error');
  },
};
